function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const SERVICE_WORKER_TIMEOUT_MS = 10000;

async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }

  await navigator.serviceWorker.register('/sw.js', {
    scope: '/'
  });

  const ready = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Service worker activation timed out')), SERVICE_WORKER_TIMEOUT_MS)
    )
  ]);

  return ready;
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }
  return navigator.serviceWorker.getRegistration('/');
}

const pushNotificationService = {
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  },

  getPermissionState(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  },

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.requestPermission();
  },

  async subscribeToPush(vapidPublicKey: string): Promise<PushSubscription> {
    const registration = await registerServiceWorker();
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      return existingSubscription;
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource
    });
  },

  async getCurrentSubscription(): Promise<PushSubscription | null> {
    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      return null;
    }
    return registration.pushManager.getSubscription();
  },

  extractSubscriptionData(subscription: PushSubscription): {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  } {
    const json = subscription.toJSON();

    if (!json.keys || !json.keys.p256dh || !json.keys.auth) {
      throw new Error('Invalid subscription: missing keys');
    }

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth
      }
    };
  }
};

export default pushNotificationService;
